import React from "react";
import { ReactNode } from "react";
import { Drawer, DrawerProps, IconButton } from "@material-ui/core";

// eslint-disable-next-line cohere-react/no-mui-styled-import
import { Block, Cancel } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "tss-react/mui";
import { PrimaryButton } from "../PrimaryButton";
import { TertiaryButton } from "../TertiaryButton";
import { Body1, Body2, H4 } from "../Typography";
import { Card } from "../Card";
import { colorsLight } from "../../themes/colors";

interface RigthDrawerProps extends DrawerProps {
  open: boolean;
  onClose: (save: boolean) => void;
  closeDisabled?: boolean;
  children: ReactNode;
  buttonText?: string;
  buttonVisible?: boolean;
  ctaErrorText?: string;
  useValidationFormat?: boolean;
  loading?: boolean;
  dynamicWidth?: number | string;
  saveAttemptError?: boolean;
  useCenteredErrorMessage?: boolean;
  reduceDividerMargin?: boolean;
  hideBackdrop?: boolean;
  backgroundColor?: string;
  reduceBottomContainerBottomPadding?: boolean;
  customDividerColor?: string;
  /** Optional secondary (e.g. "Cancel") action rendered to the left of the primary button.
   * When omitted, the footer is unchanged (single full-width primary button). */
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  secondaryButtonDisabled?: boolean;
  /** Optional header title rendered inline (left) with the close (X) button, with 12px
   * top/bottom padding. When omitted, the header is unchanged (X-only, right-aligned row). */
  titleContent?: ReactNode;
  /** Tightens the footer action buttons' vertical padding to 10px (reduces footer height /
   * scrolling). Opt-in; only applies to the secondary+primary button row. */
  compactButtons?: boolean;
  /** Footer rendered below the divider, in the bottom container. For footers the built-in
   * primary/secondary pair doesn't cover; pair it with buttonVisible={false}. Passing it
   * through children instead would place it above the divider, inside the scroll area. */
  footerContent?: ReactNode;
}

const customStyles = makeStyles<{
  backgroundColor?: string;
  reduceBottomContainerBottomPadding?: boolean;
  customDividerColor?: string;
  compactButtons?: boolean;
}>()((theme, { backgroundColor, reduceBottomContainerBottomPadding, customDividerColor, compactButtons }) => ({
  defaultBottomContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    // compactButtons: tighten the footer so the Cancel/Save area isn't ~40px tall (reduces scrolling).
    paddingBottom: compactButtons ? 10 : reduceBottomContainerBottomPadding ? theme.spacing(2) : theme.spacing(5),
    paddingRight: theme.spacing(4),
    flexWrap: "wrap",
  },
  validationBottomContainer: {
    display: "flex",
    alignItems: "start",
    width: "100%",
    paddingBottom: theme.spacing(5),
    paddingRight: theme.spacing(4),
    flexWrap: "wrap",
  },
  withCtaErrorTextStatementContainer: {
    paddingBottom: theme.spacing(10),
  },
  rightAlignedRow: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
  },
  // Header row when a titleContent is provided: title (left) inline with the close X (right).
  // 12px top/bottom padding (the card already adds top spacing). Left inset comes from the
  // card's paddingLeft (spacing(4)).
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(2.5),
  },
  drawerCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: 0,
    paddingBottom: 0,
    backgroundColor: backgroundColor || undefined,
  },
  bottomDivider: {
    width: "100%",
    marginBottom: compactButtons ? 10 : theme.spacing(5),
    marginTop: compactButtons ? 10 : theme.spacing(5),
  },
  bottomDividerRedirect: {
    background: customDividerColor || undefined,
    width: "100%",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  bottomContainer: {
    display: "flex",
    flexDirection: "column",
    zIndex: 1,
    bottom: 0,
    width: "100%",
    marginBottom: theme.spacing(1),
    paddingRight: theme.spacing(8),
    alignItems: "center",
  },
  statementContainer: {
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: theme.spacing(4),
    height: "100%",
  },
  validationIcon: {
    marginRight: theme.spacing(1),
    height: theme.spacing(2),
  },
  leftJustifiedValidationIcon: {
    marginRight: theme.spacing(1),
    height: theme.spacing(2),
    width: theme.spacing(2),
    marginTop: theme.spacing(0.0625),
  },
  errorIcon: {
    marginRight: theme.spacing(1),
  },
  justifiedIconAndText: {
    display: "flex",
    alignSelf: "start",
    justifyContent: "left",
    lineHeight: "normal",
  },
  centeredIconAndText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    lineHeight: "normal",
  },
  closeIcon: {
    height: theme.spacing(4),
    width: theme.spacing(4),
  },
  error: {
    display: "flex",
    alignItems: "center",
    paddingBottom: theme.spacing(2),
  },
  requiredMessage: {
    color: "#C62828",
    fontSize: "small",
    textAlign: "left",
  },
  cancel: {
    color: "#C62828",
    marginRight: theme.spacing(1),
  },
  leftJustifiedIconAndText: {
    display: "flex",
    alignSelf: "start",
    justifyContent: "flex-start",
    lineHeight: "normal",
    color: colorsLight.error.dark,
    paddingBottom: theme.spacing(2),
    alignItems: "center",
  },
}));

export default function RightDrawer({
  open,
  onClose,
  children,
  buttonText,
  buttonVisible = true,
  // an optional error message above "Save" button
  ctaErrorText,
  // a variation of the error state with left-justified text and a cancel icon
  useValidationFormat,
  closeDisabled,
  loading,
  dynamicWidth,
  saveAttemptError,
  useCenteredErrorMessage = false,
  reduceDividerMargin = false,
  hideBackdrop = false,
  backgroundColor,
  reduceBottomContainerBottomPadding = false,
  customDividerColor,
  secondaryButtonText,
  onSecondaryButtonClick,
  secondaryButtonDisabled,
  titleContent,
  footerContent,
  compactButtons = false,
}: RigthDrawerProps) {
  const { classes } = customStyles({
    backgroundColor,
    reduceBottomContainerBottomPadding,
    customDividerColor,
    compactButtons,
  });

  return (
    <Drawer anchor="right" open={open} onClose={() => onClose(false)} disableEnforceFocus hideBackdrop={hideBackdrop}>
      <Card style={{ width: dynamicWidth ? dynamicWidth : 485 }} className={classes.drawerCard}>
        {titleContent ? (
          <div className={classes.titleRow}>
            {titleContent}
            <IconButton onClick={() => onClose(false)} data-testid="close-right-drawer-btn">
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
        ) : (
          <div className={classes.rightAlignedRow}>
            <IconButton onClick={() => onClose(false)} data-testid="close-right-drawer-btn">
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
        )}
        <div
          className={`${classes.statementContainer} ${ctaErrorText ? classes.withCtaErrorTextStatementContainer : ""}`}
        >
          {children}
        </div>
        <div
          className={
            ctaErrorText && useValidationFormat ? classes.validationBottomContainer : classes.defaultBottomContainer
          }
        >
          <Divider
            className={`${
              saveAttemptError || reduceDividerMargin ? classes.bottomDividerRedirect : classes.bottomDivider
            } `}
          />
          {ctaErrorText &&
            !useValidationFormat &&
            (useCenteredErrorMessage ? (
              <Body2 className={classes.leftJustifiedIconAndText}>
                <Cancel className={classes.leftJustifiedValidationIcon} />
                {ctaErrorText}
              </Body2>
            ) : (
              <Body1
                className={classes.centeredIconAndText}
                style={{
                  width: dynamicWidth ? "50%" : "100%",
                }}
              >
                <Block className={classes.errorIcon} />
                {ctaErrorText}
              </Body1>
            ))}
          {ctaErrorText && useValidationFormat && (
            <Body2 className={classes.justifiedIconAndText}>
              <Cancel className={classes.validationIcon} />
              {ctaErrorText}
            </Body2>
          )}
          {saveAttemptError && (
            <div className={classes.error}>
              <Cancel className={classes.cancel} />
              <H4 className={classes.requiredMessage}>Complete Required Fields</H4>
            </div>
          )}
          {footerContent}
          {buttonVisible &&
            (secondaryButtonText && onSecondaryButtonClick ? (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, width: "100%" }}>
                <TertiaryButton
                  onClick={onSecondaryButtonClick}
                  disabled={secondaryButtonDisabled}
                  data-testid="right-drawer-secondary-btn"
                >
                  {secondaryButtonText}
                </TertiaryButton>
                <PrimaryButton
                  disabled={closeDisabled}
                  onClick={() => onClose(true)}
                  loading={Boolean(loading)}
                  data-testid="right-drawer-primary-btn"
                >
                  {buttonText ?? "Save"}
                </PrimaryButton>
              </div>
            ) : (
              <PrimaryButton
                disabled={closeDisabled}
                fullWidth
                onClick={() => onClose(true)}
                style={{ width: dynamicWidth ? "50%" : "100%" }}
                loading={Boolean(loading)}
                data-testid="right-drawer-primary-btn"
              >
                {buttonText ?? "Save"}
              </PrimaryButton>
            ))}
        </div>
      </Card>
    </Drawer>
  );
}
